import styled from "@emotion/styled";
import { Stack } from "@mui/material";
import { forwardRef, useEffect, useRef } from "react";

const StyledScrollableRoot = styled(Stack)({
  position: 'relative',
  overflow: 'hidden'
});

const StyledScrollableScrollArea = styled(Stack, {
  shouldForwardProp: prop => !['direction'].includes(prop)
})(({ direction = 'y' }) => ({
  [`overflow${direction.toUpperCase()}`]: 'auto',
}));

const StyledGradient = styled('span', {
  shouldForwardProp: prop => !['anchor'].includes(prop)
})(({ anchor = 'top' }) => ({
  position: 'absolute',
  background: 'white',
  maskImage: `linear-gradient(to ${anchor}, transparent, black)`,
  height: 30,
  width: '100%',
  [anchor]: 0,
  zIndex: 1,
  pointerEvents: 'none',
}));

const Scrollable = forwardRef((props, ref) => {
  const { children, direction = 'y', ...rest } = props;
  const scrollArea = useRef();
  const gradients = useRef([null, null, null, null]);

  const handleScroll = area => {
    const {scrollTop, scrollHeight, clientHeight} = area;
    const scrollBottom = scrollHeight - clientHeight - scrollTop;
    const scrollBarWidth = area.offsetWidth - area.clientWidth;

    [scrollTop, scrollBottom].forEach((progress, idx) => {
      gradients.current[idx].style.opacity = Math.min(progress/100, 1);
      gradients.current[idx].style.width = `calc(100% - ${scrollBarWidth}px)`;
    });
  }

  useEffect(() => {
    if (scrollArea.current) {
      const area = scrollArea.current;
      handleScroll(area);
      const onScroll = e => handleScroll(e.target);
      area.addEventListener('scroll', onScroll);
      return () => {
        area.removeEventListener('scroll', onScroll);
      }
    }
  }, []);

  return (
    <StyledScrollableRoot ref={ref} {...rest}>
      <StyledGradient ref={node => gradients.current[0] = node} anchor={'top'} />
      <StyledGradient ref={node => gradients.current[1] = node} anchor={'bottom'} />
      <StyledScrollableScrollArea ref={scrollArea} direction={direction}>
        {children}
      </StyledScrollableScrollArea>
    </StyledScrollableRoot>
  );
});

export default Scrollable;